import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSortAlphaDown, faClone } from '@fortawesome/free-solid-svg-icons';

const PAYLOAD_TYPES = [
  { value: 'simple_list', label: 'Simple list', desc: 'A list of string payloads' },
  { value: 'numbers', label: 'Numbers', desc: 'Sequential numbers with configurable range and step' },
  { value: 'null_payloads', label: 'Null payloads', desc: 'Empty string payloads repeated N times' },
];

const IntruderPayloads = ({ attackMode, positions, payloadSets, onPayloadSetsChange, isDarkMode }) => {
  const [activeSetIndex, setActiveSetIndex] = useState(0);

  // How many payload sets are needed
  const needsMultipleSets = attackMode === 'pitchfork' || attackMode === 'cluster_bomb';
  const setCount = needsMultipleSets ? Math.max(positions.length, 1) : 1;

  // Ensure payloadSets has the right number of entries
  const ensureSets = () => {
    const current = [...payloadSets];
    while (current.length < setCount) {
      current.push({
        type: 'simple_list',
        values: [],
        numberConfig: { from: 0, to: 100, step: 1, format: 'decimal', minDigits: 1 },
        nullCount: 100,
        processing: [],
      });
    }
    return current.slice(0, setCount);
  };

  const sets = ensureSets();
  const currentSet = sets[activeSetIndex] || sets[0] || {
    type: 'simple_list', values: [], numberConfig: { from: 0, to: 100, step: 1, format: 'decimal', minDigits: 1 }, nullCount: 100, processing: []
  };

  const updateCurrentSet = (updates) => {
    const newSets = [...sets];
    const idx = Math.min(activeSetIndex, newSets.length - 1);
    newSets[idx] = { ...newSets[idx], ...updates };
    onPayloadSetsChange(newSets);
  };

  const getPayloadCount = (set) => {
    if (!set) return 0;
    switch (set.type) {
      case 'simple_list': return (set.values || []).length;
      case 'numbers': {
        const cfg = set.numberConfig || {};
        const from = cfg.from || 0;
        const to = cfg.to || 100;
        const step = cfg.step || 1;
        if (step <= 0) return 0;
        return Math.floor((to - from) / step) + 1;
      }
      case 'null_payloads': return set.nullCount || 0;
      default: return 0;
    }
  };

  const generateNumberPreview = (cfg) => {
    const from = cfg?.from || 0;
    const to = cfg?.to || 100;
    const step = cfg?.step || 1;
    const format = cfg?.format || 'decimal';
    const minDigits = cfg?.minDigits || 1;

    const values = [];
    for (let i = from; i <= to && values.length < 5; i += step) {
      if (format === 'hex') {
        values.push(i.toString(16).padStart(minDigits, '0'));
      } else {
        values.push(i.toString().padStart(minDigits, '0'));
      }
    }
    return values;
  };

  const handleTextareaChange = (text) => {
    const values = text.split('\n');
    updateCurrentSet({ values });
  };

  const handleDeduplicate = () => {
    const unique = [...new Set(currentSet.values || [])];
    updateCurrentSet({ values: unique });
  };

  const handleSort = () => {
    const sorted = [...(currentSet.values || [])].sort();
    updateCurrentSet({ values: sorted });
  };

  const handleClear = () => {
    updateCurrentSet({ values: [] });
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      updateCurrentSet({ values: [...(currentSet.values || []), ...lines] });
    } catch {}
  };

  return (
    <div className="space-y-3">
      {/* Payload set tabs (for pitchfork/cluster_bomb) */}
      {needsMultipleSets && (
        <div className="flex gap-1 flex-wrap">
          {Array.from({ length: setCount }).map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveSetIndex(i)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                activeSetIndex === i
                  ? (isDarkMode ? 'bg-orange-900/30 text-orange-300 border border-orange-500' : 'bg-orange-50 text-orange-700 border border-orange-400')
                  : (isDarkMode ? 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300')
              }`}
            >
              Payload set {i + 1}
              <span className="ml-1.5 opacity-60">({getPayloadCount(sets[i])})</span>
            </button>
          ))}
        </div>
      )}

      {/* Payload type selector */}
      <div>
        <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Payload type
        </label>
        <select
          value={currentSet.type}
          onChange={e => updateCurrentSet({ type: e.target.value })}
          className={`text-xs rounded border px-2 py-1.5 w-64 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
        >
          {PAYLOAD_TYPES.map(pt => (
            <option key={pt.value} value={pt.value}>{pt.label}</option>
          ))}
        </select>
        <span className={`text-[10px] ml-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {PAYLOAD_TYPES.find(pt => pt.value === currentSet.type)?.desc}
        </span>
      </div>

      {/* Type-specific config */}
      {currentSet.type === 'simple_list' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className={`text-[10px] font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Payload values ({(currentSet.values || []).filter(Boolean).length} items)
            </label>
            <div className="flex gap-1">
              <button onClick={handlePaste} className={`px-2 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                <FontAwesomeIcon icon={faClone} className="mr-1" />Paste
              </button>
              <button onClick={handleDeduplicate} className={`px-2 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                Deduplicate
              </button>
              <button onClick={handleSort} className={`px-2 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                <FontAwesomeIcon icon={faSortAlphaDown} className="mr-1" />Sort
              </button>
              <button onClick={handleClear} className={`px-2 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-gray-700 text-red-400 hover:bg-gray-600' : 'bg-gray-200 text-red-500 hover:bg-gray-300'}`}>
                <FontAwesomeIcon icon={faTrash} className="mr-1" />Clear
              </button>
            </div>
          </div>
          <textarea
            value={(currentSet.values || []).join('\n')}
            onChange={e => handleTextareaChange(e.target.value)}
            rows={12}
            spellCheck={false}
            className={`w-full font-mono text-xs rounded border px-3 py-2 resize-y ${
              isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
            }`}
            placeholder="Enter one payload per line..."
          />
        </div>
      )}

      {currentSet.type === 'numbers' && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>From</label>
              <input
                type="number"
                value={currentSet.numberConfig?.from ?? 0}
                onChange={e => updateCurrentSet({ numberConfig: { ...currentSet.numberConfig, from: Number(e.target.value) } })}
                className={`w-full text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
              />
            </div>
            <div>
              <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>To</label>
              <input
                type="number"
                value={currentSet.numberConfig?.to ?? 100}
                onChange={e => updateCurrentSet({ numberConfig: { ...currentSet.numberConfig, to: Number(e.target.value) } })}
                className={`w-full text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
              />
            </div>
            <div>
              <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Step</label>
              <input
                type="number"
                value={currentSet.numberConfig?.step ?? 1}
                onChange={e => updateCurrentSet({ numberConfig: { ...currentSet.numberConfig, step: Number(e.target.value) } })}
                min={1}
                className={`w-full text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Format</label>
              <select
                value={currentSet.numberConfig?.format || 'decimal'}
                onChange={e => updateCurrentSet({ numberConfig: { ...currentSet.numberConfig, format: e.target.value } })}
                className={`w-full text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
              >
                <option value="decimal">Decimal</option>
                <option value="hex">Hexadecimal</option>
              </select>
            </div>
            <div>
              <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Min digits</label>
              <input
                type="number"
                value={currentSet.numberConfig?.minDigits ?? 1}
                onChange={e => updateCurrentSet({ numberConfig: { ...currentSet.numberConfig, minDigits: Number(e.target.value) } })}
                min={1}
                max={10}
                className={`w-full text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
              />
            </div>
          </div>

          {/* Number preview */}
          <div>
            <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Preview ({getPayloadCount(currentSet)} payloads)
            </label>
            <div className={`font-mono text-xs p-2 rounded border ${isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
              {generateNumberPreview(currentSet.numberConfig).map((v, i) => (
                <span key={i}>{v}{i < 4 ? ', ' : ''}</span>
              ))}
              {getPayloadCount(currentSet) > 5 && <span className="opacity-50"> ... ({getPayloadCount(currentSet)} total)</span>}
            </div>
          </div>
        </div>
      )}

      {currentSet.type === 'null_payloads' && (
        <div>
          <label className={`text-[10px] font-semibold block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Number of null (empty) payloads
          </label>
          <input
            type="number"
            value={currentSet.nullCount ?? 100}
            onChange={e => updateCurrentSet({ nullCount: Number(e.target.value) })}
            min={1}
            className={`w-32 text-xs rounded border px-2 py-1.5 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`}
          />
          <span className={`text-[10px] ml-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Will send {currentSet.nullCount || 0} requests with empty payloads
          </span>
        </div>
      )}
    </div>
  );
};

export default IntruderPayloads;
